// Optimistically append the message to root.messagesByChannel, then INSERT
// via the SDK. The realtime subscription (subscribeRealtime) will see the
// inserted row come back and reconcile the optimistic id.
import { getSDK } from '../sdk.js'
import { rowToMessage } from './chatUtils.js'

export const sendMessage = async function ({ channelId, body, replyTo = null, threadRoot = null }) {
  const root = this.state.root
  // Pull pending attachments — strip the upload-tracking fields, keep what
  // we want stored on the message row's `attachments` jsonb column.
  const pending = (root.pendingAttachments && root.pendingAttachments[channelId]) || []
  const attachments = pending
    .filter(a => a && a.url && !a.uploading)
    .map(a => ({ name: a.name, size: a.size, mime: a.mime, url: a.url }))
  const trimmed = (body || '').trim()
  // Allow attachment-only messages with empty body.
  if (!trimmed && attachments.length === 0) return null
  if (!channelId) return null
  const author = root.currentUserEmail
  const tempId = `tmp-${Date.now()}`
  const now = new Date().toISOString()

  const optimistic = {
    id: tempId,
    channelId,
    authorEmail: author,
    body: trimmed,
    attachments: attachments.length ? attachments : null,
    replyTo,
    threadRoot,
    createdAt: now,
    _optimistic: true
  }
  const prev = root.messagesByChannel[channelId] || []
  root.update({
    messagesByChannel: { ...root.messagesByChannel, [channelId]: [...prev, optimistic] },
    pendingAttachments: (() => {
      const next = { ...root.pendingAttachments }
      delete next[channelId]
      return next
    })()
  })

  try {
    // migrated 2026-04-27 to SDK route workspaceProject.chat.messages create
    const data = await getSDK().execute('workspaceProject.chat.messages', 'create', {
      channelId,
      payload: {
        author_email: author,
        body: trimmed,
        attachments: attachments.length ? attachments : null,
        reply_to: replyTo,
        thread_root: threadRoot
      }
    })
    // Reconcile with whatever is in state right now. Three possible states:
    //   1. Optimistic row still in list, realtime has not arrived → swap.
    //   2. Realtime arrived first and appended the real row → remove the
    //      optimistic row without re-adding (would otherwise duplicate).
    //   3. Optimistic row gone, no real row yet → append.
    const real = rowToMessage(data?.data || data)
    const current = root.messagesByChannel[channelId] || []
    const hasReal = current.some(m => m.id === real.id)
    const next = hasReal
      ? current.filter(m => m.id !== tempId)
      : current.map(m => m.id === tempId ? real : m)
    root.update({ messagesByChannel: { ...root.messagesByChannel, [channelId]: next } })
    return real.id
  } catch (e) {
    console.error('[sendMessage]', e?.message || e)
    // Rollback: drop the optimistic row.
    const list = (root.messagesByChannel[channelId] || []).filter(m => m.id !== tempId)
    root.update({ messagesByChannel: { ...root.messagesByChannel, [channelId]: list } })
    return null
  }
}
